import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

// Sprint 8 — KPIStrip réutilisable (companies temperatures, dev statuses,
// contacts buckets...). Inspiré ent-shared.jsx KPIStrip + dev maquettes.
//
// Cellule clickable : applique le filtre quick. Si onClick est défini,
// la cellule devient hoverable + clickable. Variante "active" : bg pastel.
//
// Sprint 10.x — Étendu pour /companies (rangée "Valeur") :
// - `icon` : Composant lucide ou élément quelconque (affiché à la place
//   du dot quand fourni, accent violet doux par défaut).
// - `trailing` : slot libre rendu sous le `pct` (utilisé pour le toggle
//   3 formats du KPI CA récurrent ; n'affecte pas le clic de la cellule).

export interface KPIItem {
  key: string;
  label: string;
  value: number | string;
  pct?: string;
  dotClass?: string;
  /** Icône lucide ou ReactNode. Si fourni, remplace `dotClass`. */
  icon?: ReactNode;
  /** Tonalité de l'icône (text-*). Défaut violet. */
  iconClass?: string;
  /** Slot rendu sous `pct` — ex: segmented control de format. */
  trailing?: ReactNode;
  active?: boolean;
  onClick?: () => void;
}

interface KPIStripProps {
  items: KPIItem[];
  className?: string;
}

const CELL_BASE =
  "flex-1 min-w-[140px] flex flex-col items-start gap-0.5 rounded-xl border px-3.5 py-2.5 transition-colors text-left";

export const KPIStrip = ({ items, className }: KPIStripProps) => (
  <div
    className={cn("flex flex-wrap gap-2 items-stretch", className)}
    role="list"
  >
    {items.map((item) => {
      const labelNode = (
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5">
          {item.icon ? (
            <span
              className={cn(
                "inline-flex items-center justify-center shrink-0 [&_svg]:size-3.5",
                item.iconClass ?? "text-violet-600 dark:text-violet-300",
              )}
            >
              {item.icon}
            </span>
          ) : item.dotClass ? (
            <span className={cn("size-1.5 rounded-full", item.dotClass)} />
          ) : null}
          {item.label}
        </span>
      );
      const valueNode = (
        <span className="text-[20px] font-semibold text-foreground tabular-nums leading-none mt-1">
          {item.value}
        </span>
      );
      const pctNode = item.pct ? (
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {item.pct}
        </span>
      ) : null;

      const cellClasses = cn(
        CELL_BASE,
        item.active
          ? "bg-violet-50 border-violet-200 dark:bg-violet-950/30 dark:border-violet-900/40"
          : "bg-card border-border",
        item.onClick && !item.active && "hover:bg-muted/50 cursor-pointer",
        item.onClick && item.active && "hover:bg-violet-100/80 cursor-pointer",
      );

      // Sprint 10.x — Si `trailing` est présent on doit éviter le nesting
      // <button> dans <button> (HTML invalide → erreur React hydration). On
      // rend alors un wrapper <div> + un <button> sur la zone label/value/pct
      // seulement, avec le trailing en sibling cliquable indépendamment.
      if (item.trailing) {
        return (
          <div key={item.key} role="listitem" className={cellClasses}>
            {item.onClick ? (
              <button
                type="button"
                onClick={item.onClick}
                className="w-full flex flex-col items-start gap-0.5 text-left rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
              >
                {labelNode}
                {valueNode}
                {pctNode}
              </button>
            ) : (
              <div className="w-full flex flex-col items-start gap-0.5">
                {labelNode}
                {valueNode}
                {pctNode}
              </div>
            )}
            <div className="mt-1.5 w-full">{item.trailing}</div>
          </div>
        );
      }

      const Tag = item.onClick ? "button" : "div";
      return (
        <Tag
          key={item.key}
          role="listitem"
          type={item.onClick ? "button" : undefined}
          onClick={item.onClick}
          className={cellClasses}
        >
          {labelNode}
          {valueNode}
          {pctNode}
        </Tag>
      );
    })}
  </div>
);
